import { DateObjectUnits, DateTime } from 'luxon'
import React from 'react'
import { usePopper } from '../Popover/hooks/usePopper'
import { Calendar } from '../Calendar/Calendar'
import { Popover } from '../Popover/Popover'

const userAgent = navigator.userAgent
const hasNativeSupport =
  /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(userAgent) ||
  /\b(Android|Windows Phone|iPad|iPod)\b/i.test(userAgent) ||
  /\b(Chrome\/((8[3-9])|(9[0-9])|([0-9][0-9][0-9])))\b/i.test(userAgent) ||
  /\b(Version\/((14.[1-9].[0-9])|(14.[1-9])|(1[5-9].[0-9])|([2-9][0-9].[0-9])|([0-9][0-9][0-9].[0-9]))( Mobile\/15E148 | )Safari)\b/i.test(
    userAgent,
  )
const isSafari =
  /\b(Version\/(([0-9][0-9].[0-9].[0-9])|([0-9][0-9].[0-9])|([0-9][0-9][0-9].[0-9]))( Mobile\/15E148 | )Safari)\b/i.test(
    userAgent,
  )
const dateStringToDateTime = (
  val?: string,
  initialValue?: DateObjectUnits,
  zone?: string,
): DateTime | undefined => {
  if (!val) return undefined
  const value: DateTime = DateTime.fromFormat(val, 'yyyy-MM-dd')
  if (!value.isValid) return undefined
  return DateTime.fromObject(
    {
      year: value.isValid ? value.year || initialValue?.year : undefined,
      month: value.isValid ? value.month || initialValue?.month : undefined,
      day: value.isValid ? value.day || initialValue?.day : undefined,
    },
    { zone },
  ).startOf('hour')
}

type DetailedInputHTMLAttributes = Omit<
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'value'
>

interface IProps extends DetailedInputHTMLAttributes {
  value?: DateTime
  disabledPopover?: boolean
  DayCell?: (props: { className?: string; value: DateTime }) => React.ReactElement
  onChangeValue?: (date: DateTime | undefined, evt?: React.ChangeEvent<HTMLInputElement>) => void
  onBlurValue?: (date: DateTime | undefined, evt: React.FocusEvent<HTMLInputElement>) => void
}

export const DateInput = React.memo<IProps>(
  ({
    value: initialValue,
    disabledPopover,
    className = 'form-control',
    onChangeValue: handleChangeValue,
    onBlurValue: handleBlurValue,
    DayCell,
    ...props
  }: IProps): React.ReactElement => {
    const objectValue = initialValue?.toObject()
    const zone = initialValue?.zoneName || undefined
    const { target, toggle, isOpen, targetProps, ...tooltipProps } = usePopper<HTMLInputElement>({
      placement: 'top',
    })
    const value =
      typeof objectValue?.year !== 'undefined' &&
      typeof objectValue?.month !== 'undefined' &&
      typeof objectValue?.day !== 'undefined'
        ? DateTime.fromObject(objectValue, { zone })
        : undefined
    if (!hasNativeSupport) {
      className += ' unsupported-date'
    }
    if (isSafari) {
      className += ' safari-date'
    }
    return (
      <>
        <input
          ref={target}
          {...props}
          {...targetProps}
          type={props.type || 'date'}
          className={className}
          onClick={!hasNativeSupport && !disabledPopover ? () => toggle() : props?.onClick}
          onChange={
            handleChangeValue
              ? (evt: React.ChangeEvent<HTMLInputElement>) => {
                  const val: string = evt.target.value
                  const date = dateStringToDateTime(val, objectValue, zone)
                  if (date) {
                    handleChangeValue(date, evt)
                    if (isOpen) {
                      toggle()
                    }
                  }
                }
              : props.onChange
          }
          onBlur={
            handleBlurValue
              ? (evt: React.FocusEvent<HTMLInputElement>) => {
                  handleBlurValue(dateStringToDateTime(evt.target.value, objectValue, zone), evt)
                }
              : props.onBlur
          }
          value={value?.toFormat('yyyy-MM-dd') || ''}
          placeholder="Select a date"
          list="date-select"
          pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}"
        />
        {!hasNativeSupport && !disabledPopover && (
          <Popover {...tooltipProps} isOpen={isOpen} toggle={toggle}>
            <Calendar
              value={value ? [value] : undefined}
              onSelect={(date) => {
                if (handleChangeValue && date.isValid) {
                  handleChangeValue(date?.setZone(zone).startOf('hour'))
                }
                toggle()
              }}
              DayCell={DayCell}
            />
          </Popover>
        )}
      </>
    )
  },
  (prevProps, nextProps) =>
    prevProps.value?.toMillis() === nextProps.value?.toMillis() &&
    prevProps.className === nextProps.className &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.onChangeValue === nextProps.onChangeValue &&
    prevProps.onBlurValue === nextProps.onBlurValue &&
    prevProps.onChange === nextProps.onChange &&
    prevProps.onBlur === nextProps.onBlur,
)

DateInput.displayName = 'DateInput'
